import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { ChevronLeft, ChevronRight, Plus, Edit, Trash2, Calendar, CalendarX2, Repeat, X, Code, Copy, Check, ExternalLink, Lock, Wrench, AlertTriangle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BASE_URL = process.env.REACT_APP_BACKEND_URL?.replace('/api', '') || window.location.origin;

// Calendar constants
const HOURS = Array.from({ length: 16 }, (_, i) => i + 6); // 6:00 - 21:00
const DAYS = ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag', 'Sonntag'];

const ResourcesPage = () => {
  const [resources, setResources] = useState([]);
  const [studios, setStudios] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [selectedResource, setSelectedResource] = useState(null);
  const [currentWeekStart, setCurrentWeekStart] = useState(getWeekStart(new Date()));
  const [loading, setLoading] = useState(true);
  
  // Dialogs
  const [resourceDialogOpen, setResourceDialogOpen] = useState(false);
  const [bookingDialogOpen, setBookingDialogOpen] = useState(false);
  const [embedDialogOpen, setEmbedDialogOpen] = useState(false);
  const [editingResource, setEditingResource] = useState(null);
  const [editingBooking, setEditingBooking] = useState(null);
  const [copiedEmbed, setCopiedEmbed] = useState(false);
  
  // Form states
  const [resourceForm, setResourceForm] = useState({ name: '', description: '', color: '#3B82F6', type: 'room', capacity: 1 });
  // Block C: Ressourcen-Sperren (Defekt-Meldung)
  const [blockDialogOpen, setBlockDialogOpen] = useState(false);
  const [blockResource, setBlockResource] = useState(null);
  const [resourceBlocks, setResourceBlocks] = useState([]);
  const [blockForm, setBlockForm] = useState({ start: '', end: '', reason: '' });
  const [deleteConfirmStep, setDeleteConfirmStep] = useState(0); // 0 = no delete, 1-3 = confirmation steps
  const [bookingForm, setBookingForm] = useState({
    title: '',
    description: '',
    date: '',
    startTime: '09:00',
    endTime: '10:00',
    isRecurring: false,
    recurringType: 'weekly',
    recurringUntil: '',
    color: '',
    // Contact/Trainer info for public display
    contactName: '',
    contactPhone: '',
    contactEmail: '',
    contactWebsite: '',
    registrationLink: '',
    registrationInfo: '',
    isPublic: true,
    maxParticipants: '',
    currentParticipants: '',
    isPrivate: false,
    whatsappOptin: false,
    notifyEmail: true
  });
  const [editScope, setEditScope] = useState('all');
  
  // Get current user role
  const [userRole, setUserRole] = useState('');
  // Block C: Embed-Isolation (Mandant/Studio) + Defekt-Badges + Tagesauslastung
  const [embedTenantSlug, setEmbedTenantSlug] = useState('');
  const [embedStudioId, setEmbedStudioId] = useState('');
  const [activeBlocks, setActiveBlocks] = useState({});
  const [showUtilization, setShowUtilization] = useState(false);
  const [utilization, setUtilization] = useState([]);
  const [utilDate, setUtilDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [utilLoading, setUtilLoading] = useState(false);
  const [utilView, setUtilView] = useState('day'); // 'day' | 'week'
  const [weekUtil, setWeekUtil] = useState([]); // [{ date, resources }]
  
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        setUserRole(payload.role || '');
      } catch (e) {
        console.error('Error parsing token:', e);
      }
    }
  }, []);

  // Get Monday of the week
  function getWeekStart(date) {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    d.setDate(diff);
    d.setHours(0, 0, 0, 0); // Reset time to midnight
    return d;
  }

  // DST-safe: add/subtract whole calendar days (NOT fixed milliseconds).
  // Using getTime()+N*86400000 breaks across DST changes (e.g. 25.10),
  // shifting Monday 00:00 to Sunday 23:00 and offsetting the whole grid.
  function addDays(date, days) {
    const d = new Date(date);
    d.setDate(d.getDate() + days);
    d.setHours(0, 0, 0, 0);
    return d;
  }

  // Fetch resources
  const fetchResources = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/resources`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setResources(response.data);
      if (response.data.length > 0 && !selectedResource) {
        setSelectedResource(response.data[0]);
      }
    } catch (error) {
      console.error('Error fetching resources:', error);
      toast.error('Fehler beim Laden der Ressourcen');
    }
  }, [selectedResource]);

  // Fetch bookings
  const fetchBookings = useCallback(async () => {
    if (!selectedResource) return;
    
    try {
      const token = localStorage.getItem('token');
      const startDate = currentWeekStart.toISOString();
      const endDate = addDays(currentWeekStart, 7).toISOString();
      
      const response = await axios.get(`${API}/resources/bookings`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          resource_id: selectedResource.id,
          start_date: startDate,
          end_date: endDate
        }
      });
      setBookings(response.data);
    } catch (error) {
      console.error('Error fetching bookings:', error);
      toast.error('Fehler beim Laden der Buchungen');
    } finally {
      setLoading(false);
    }
  }, [selectedResource, currentWeekStart]);

  useEffect(() => {
    fetchResources();
    const token = localStorage.getItem('token');
    axios.get(`${API}/studios`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => setStudios(Array.isArray(res.data) ? res.data : (res.data.studios || [])))
      .catch(() => setStudios([]));
    axios.get(`${API}/resources/embed-info`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => setEmbedTenantSlug(res.data?.tenant_slug || ''))
      .catch(() => setEmbedTenantSlug(''));
    loadActiveBlocks();
  }, []);

  // Block C: aktive Sperren (Defekt) laden → rote Markierung in Liste/Plan
  const loadActiveBlocks = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.get(`${API}/resources/blocks`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const nowIso = new Date().toISOString();
      const map = {};
      (Array.isArray(data) ? data : []).forEach((b) => {
        const started = !b.start || b.start <= nowIso;
        const notEnded = !b.end || b.end >= nowIso;
        if (started && notEnded && !map[b.resource_id]) map[b.resource_id] = b;
      });
      setActiveBlocks(map);
    } catch (e) {
      setActiveBlocks({});
    }
  }, []);

  // Block C: Tagesauslastung laden
  const fetchUtilization = useCallback(async (dateStr) => {
    setUtilLoading(true);
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.get(`${API}/resources/utilization`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { date: dateStr },
      });
      setUtilization(data?.resources || []);
    } catch (e) {
      setUtilization([]);
    } finally {
      setUtilLoading(false);
    }
  }, []);

  // Block C: Wochen-Auslastung laden (Mo–So der Woche von dateStr)
  const fetchWeekUtilization = useCallback(async (dateStr) => {
    setUtilLoading(true);
    try {
      const token = localStorage.getItem('token');
      const base = new Date(dateStr);
      const dow = (base.getDay() + 6) % 7; // Montag = 0
      const monday = addDays(base, -dow);
      const dates = Array.from({ length: 7 }, (_, i) => addDays(monday, i).toISOString().slice(0, 10));
      const results = await Promise.all(
        dates.map((d) =>
          axios
            .get(`${API}/resources/utilization`, {
              headers: { Authorization: `Bearer ${token}` },
              params: { date: d },
            })
            .then((res) => ({ date: d, resources: res.data?.resources || [] }))
            .catch(() => ({ date: d, resources: [] }))
        )
      );
      setWeekUtil(results);
    } catch (e) {
      setWeekUtil([]);
    } finally {
      setUtilLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!showUtilization) return;
    if (utilView === 'week') fetchWeekUtilization(utilDate);
    else fetchUtilization(utilDate);
  }, [showUtilization, utilDate, utilView, fetchUtilization, fetchWeekUtilization]);

  // iFrame-/Link-URL mit Mandant (Pflicht) + optional Studio bauen
  const buildKursplanUrl = (embed) => {
    const params = new URLSearchParams();
    if (embedTenantSlug) params.set('tenant', embedTenantSlug);
    if (embedStudioId) params.set('studio', embedStudioId);
    if (embed) params.set('embed', 'true');
    const qs = params.toString();
    return `${BASE_URL}/kursplan${qs ? `?${qs}` : ''}`;
  };

  // Stunde (als Dezimal) aus ISO — gleiche Konvention wie der Belegungs-Kalender (getHours)
  const swissHour = (iso) => {
    try {
      const d = new Date(iso);
      return d.getHours() + d.getMinutes() / 60;
    } catch (e) {
      return 0;
    }
  };

  useEffect(() => {
    if (selectedResource) {
      fetchBookings();
    }
  }, [selectedResource, currentWeekStart, fetchBookings]);

  // Navigate weeks
  const goToPreviousWeek = () => {
    setCurrentWeekStart(addDays(currentWeekStart, -7));
  };

  const goToNextWeek = () => {
    setCurrentWeekStart(addDays(currentWeekStart, 7));
  };

  const goToToday = () => {
    setCurrentWeekStart(getWeekStart(new Date()));
  };

  // Get dates for current week
  const getWeekDates = () => {
    return Array.from({ length: 7 }, (_, i) => {
      const date = new Date(currentWeekStart);
      date.setDate(date.getDate() + i);
      return date;
    });
  };

  // Resource CRUD
  const handleSaveResource = async () => {
    try {
      const token = localStorage.getItem('token');
      
      if (editingResource) {
        await axios.put(`${API}/resources/${editingResource.id}`, { ...resourceForm, studio_id: resourceForm.studio_id || null }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Ressource aktualisiert');
      } else {
        await axios.post(`${API}/resources`, { ...resourceForm, studio_id: resourceForm.studio_id || null, type: resourceForm.type || 'room' }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Ressource erstellt');
      }
      
      setResourceDialogOpen(false);
      setEditingResource(null);
      setResourceForm({ name: '', description: '', color: '#3B82F6', studio_id: '', type: 'room', capacity: 1 });
      fetchResources();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
    }
  };

  // Block C: Defekt-Meldung / Sperren
  const openBlockDialog = async (resource) => {
    setBlockResource(resource);
    setBlockForm({ start: '', end: '', reason: '' });
    setBlockDialogOpen(true);
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.get(`${API}/resources/blocks?resource_id=${resource.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setResourceBlocks(Array.isArray(data) ? data : []);
    } catch (e) {
      setResourceBlocks([]);
    }
  };

  const handleSaveBlock = async () => {
    try {
      const token = localStorage.getItem('token');
      const payload = {
        start: blockForm.start ? new Date(blockForm.start).toISOString() : null,
        end: blockForm.end ? new Date(blockForm.end).toISOString() : null,
        reason: blockForm.reason,
      };
      const { data } = await axios.post(`${API}/resources/${blockResource.id}/block`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Ressource gesperrt');
      setResourceBlocks((prev) => [...prev, data.block]);
      setBlockForm({ start: '', end: '', reason: '' });
      loadActiveBlocks();
      if (showUtilization) {
        if (utilView === 'week') fetchWeekUtilization(utilDate);
        else fetchUtilization(utilDate);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Sperren');
    }
  };

  const handleDeleteBlock = async (blockId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/resources/blocks/${blockId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setResourceBlocks((prev) => prev.filter((b) => b.id !== blockId));
      toast.success('Sperre aufgehoben');
      loadActiveBlocks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Aufheben');
    }
  };

  const handleDeleteResource = async (id) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/resources/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Ressource gelöscht');
      fetchResources();
      if (selectedResource?.id === id) {
        setSelectedResource(null);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Löschen');
    }
  };

  // Booking CRUD
  const handleSaveBooking = async () => {
    if (!selectedResource) return;
    
    try {
      const token = localStorage.getItem('token');
      
      const startDateTime = new Date(`${bookingForm.date}T${bookingForm.startTime}:00`);
      const endDateTime = new Date(`${bookingForm.date}T${bookingForm.endTime}:00`);
      
      const payload = {
        resource_id: selectedResource.id,
        title: bookingForm.title,
        description: bookingForm.description,
        start_time: startDateTime.toISOString(),
        end_time: endDateTime.toISOString(),
        is_recurring: bookingForm.isRecurring,
        recurring_type: bookingForm.isRecurring ? bookingForm.recurringType : 'none',
        recurring_until: bookingForm.isRecurring && bookingForm.recurringUntil
          ? new Date(`${bookingForm.recurringUntil}T23:59:59`).toISOString()
          : null,
        color: bookingForm.color || null,
        // Contact/Trainer info
        contact_name: bookingForm.contactName || null,
        contact_phone: bookingForm.contactPhone || null,
        contact_email: bookingForm.contactEmail || null,
        contact_website: bookingForm.contactWebsite || null,
        registration_link: bookingForm.registrationLink || null,
        registration_info: bookingForm.registrationInfo || null,
        is_public: bookingForm.isPublic,
        max_participants: bookingForm.maxParticipants ? parseInt(bookingForm.maxParticipants) : null,
        current_participants: bookingForm.currentParticipants !== '' ? parseInt(bookingForm.currentParticipants) : null,
        is_private: bookingForm.isPrivate,
        whatsapp_optin: bookingForm.whatsappOptin,
        notify: true,
        notify_email: bookingForm.notifyEmail,
        notify_whatsapp: bookingForm.whatsappOptin
      };
      
      if (editingBooking) {
        let putUrl = `${API}/resources/bookings/${editingBooking.id}`;
        if (editingBooking.is_recurring && editScope === 'this_and_following') {
          const occ = editingBooking.instance_date || toLocalYMD(new Date(editingBooking.start_time));
          putUrl += `?edit_scope=this_and_following&occurrence_date=${occ}`;
        }
        await axios.put(putUrl, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success(editingBooking.is_recurring && editScope === 'this_and_following'
          ? 'Serie ab diesem Termin geändert'
          : 'Buchung aktualisiert');
      } else {
        await axios.post(`${API}/resources/bookings`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success(bookingForm.isRecurring ? 'Wiederkehrende Buchung erstellt' : 'Buchung erstellt');
      }
      
      setBookingDialogOpen(false);
      setEditingBooking(null);
      resetBookingForm();
      fetchBookings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
    }
  };

  const handleDeleteBooking = async (booking) => {
    const isRecurring = booking.is_recurring;
    let deleteMessage = 'Buchung wirklich löschen?';
    let deleteSeries = false;
    
    if (isRecurring) {
      const choice = window.confirm('Diese Buchung ist wiederkehrend. "OK" löscht ALLE Wiederholungen. Abbrechen zum Behalten.');
      if (!choice) return;
      if (!window.confirm('Bist du WIRKLICH sicher? ALLE Wiederholungen dieser Serie werden unwiderruflich gelöscht. Dies kann NICHT rückgängig gemacht werden.')) return;
      deleteSeries = true;
    } else {
      if (!window.confirm(deleteMessage)) return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/resources/bookings/${booking.id}?delete_series=${deleteSeries}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Buchung gelöscht');
      fetchBookings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Löschen');
    }
  };

  const handleCancelOccurrence = async (booking) => {
    const occDate = booking.instance_date || toLocalYMD(new Date(booking.start_time));
    if (!window.confirm(`Nur diesen einen Termin (${occDate}) absagen? Die übrige Serie bleibt bestehen. Die reservierende Person wird benachrichtigt.`)) return;
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/resources/bookings/${booking.id}/cancel-occurrence?occurrence_date=${occDate}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Einzeltermin abgesagt');
      setBookingDialogOpen(false);
      setEditingBooking(null);
      fetchBookings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Absagen');
    }
  };

  const resetBookingForm = () => {
    setBookingForm({
      title: '',
      description: '',
      date: '',
      startTime: '09:00',
      endTime: '10:00',
      isRecurring: false,
      recurringType: 'weekly',
      recurringUntil: '',
      color: '',
      contactName: '',
      contactPhone: '',
      contactEmail: '',
      contactWebsite: '',
      registrationLink: '',
      registrationInfo: '',
      isPublic: true,
      maxParticipants: '',
      isPrivate: false,
      whatsappOptin: false,
      notifyEmail: true
    });
    setEditScope('all');
  };

  // Lokales Datum als YYYY-MM-DD (NICHT toISOString → sonst UTC-Shift = ein Tag zu früh)
  const toLocalYMD = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  // Open booking dialog with date/time pre-filled
  const handleCellClick = (date, hour) => {
    const dateStr = toLocalYMD(date);
    setBookingForm({
      ...bookingForm,
      date: dateStr,
      startTime: `${hour.toString().padStart(2, '0')}:00`,
      endTime: `${(hour + 1).toString().padStart(2, '0')}:00`
    });
    setEditingBooking(null);
    setBookingDialogOpen(true);
  };

  // Edit existing booking
  const handleEditBooking = (booking) => {
    const startDate = new Date(booking.start_time);
    const endDate = new Date(booking.end_time);
    
    setBookingForm({
      title: booking.title,
      description: booking.description || '',
      date: toLocalYMD(startDate),
      startTime: startDate.toTimeString().slice(0, 5),
      endTime: endDate.toTimeString().slice(0, 5),
      isRecurring: booking.is_recurring || false,
      recurringType: booking.recurring_type || 'weekly',
      recurringUntil: booking.recurring_until ? toLocalYMD(new Date(booking.recurring_until)) : '',
      color: booking.color || '',
      contactName: booking.contact_name || '',
      contactPhone: booking.contact_phone || '',
      contactEmail: booking.contact_email || '',
      contactWebsite: booking.contact_website || '',
      registrationLink: booking.registration_link || '',
      registrationInfo: booking.registration_info || '',
      isPublic: booking.is_public !== false,
      maxParticipants: booking.max_participants || '',
      currentParticipants: booking.current_participants ?? '',
      isPrivate: booking.is_private || false,
      whatsappOptin: booking.whatsapp_optin || false,
      notifyEmail: booking.notify_email !== false
    });
    setEditScope('all');
    setEditingBooking(booking);
    setBookingDialogOpen(true);
  };

  // Get bookings for a specific cell
  const getBookingsForCell = (date, hour) => {
    return bookings.filter(booking => {
      const start = new Date(booking.start_time);
      const end = new Date(booking.end_time);
      
      // Create cell boundaries using the same day as the booking
      const cellStart = new Date(date);
      cellStart.setHours(hour, 0, 0, 0);
      const cellEnd = new Date(date);
      cellEnd.setHours(hour + 1, 0, 0, 0);
      
      // Compare just the date portion (year, month, day)
      const startDate = new Date(start.getFullYear(), start.getMonth(), start.getDate());
      const cellDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
      const isSameDay = startDate.getTime() === cellDate.getTime();
      
      // Check if the booking overlaps with this cell's hour
      const startHour = start.getHours() + start.getMinutes() / 60;
      const endHour = end.getHours() + end.getMinutes() / 60;
      const overlapsHour = startHour < (hour + 1) && endHour > hour;
      
      return isSameDay && overlapsHour;
    });
  };

  // Format date for display (with year)
  const formatDate = (date, includeYear = false) => {
    if (includeYear) {
      return date.toLocaleDateString('de-CH', { day: '2-digit', month: '2-digit', year: 'numeric' });
    }
    return date.toLocaleDateString('de-CH', { day: '2-digit', month: '2-digit' });
  };

  // Check if date is today
  const isToday = (date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const weekDates = getWeekDates();

  return (
    <div className="space-y-4" data-testid="resources-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold">Ressourcen</h1>
          <p className="text-gray-600 text-sm">Raumverwaltung und Belegungsplan</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant={showUtilization ? 'default' : 'outline'}
            onClick={() => setShowUtilization((v) => !v)}
            title="Tagesauslastung aller Ressourcen"
            data-testid="toggle-utilization-btn"
          >
            <Calendar className="h-4 w-4 mr-2" />
            Tagesauslastung
          </Button>
          <Button 
            variant="outline"
            onClick={() => setEmbedDialogOpen(true)}
            title="Kursplan einbetten"
          >
            <Code className="h-4 w-4 mr-2" />
            Einbetten
          </Button>
          {['admin', 'trainer'].includes(userRole) && (
            <Button 
              className="w-full sm:w-auto"
              onClick={() => {
                setEditingResource(null);
                setResourceForm({ name: '', description: '', color: '#3B82F6', studio_id: '', type: 'room', capacity: 1 });
                setResourceDialogOpen(true);
              }}>
              <Plus className="h-4 w-4 mr-2" />
              Neue Ressource
            </Button>
          )}
        </div>
      </div>

      {/* Resource Selection */}
      {resources.length > 0 && (
        <div className="flex gap-2 flex-wrap items-center">
          {resources.map(resource => (
            <div key={resource.id} className="flex items-center gap-1">
              <Button
                variant={selectedResource?.id === resource.id ? 'default' : 'outline'}
                onClick={() => setSelectedResource(resource)}
                className="flex items-center gap-2"
              >
                <div 
                  className="w-3 h-3 rounded-full" 
                  style={{ backgroundColor: resource.color || '#3B82F6' }}
                />
                {resource.name}
              </Button>
              {['admin', 'trainer'].includes(userRole) && (
                <button
                  className="p-1 rounded hover:bg-gray-100"
                  title="Bearbeiten"
                  onClick={() => {
                    setEditingResource(resource);
                    setResourceForm({ 
                      name: resource.name, 
                      description: resource.description || '',
                      color: resource.color || '#3B82F6',
                      studio_id: resource.studio_id || '',
                      type: resource.type || 'room',
                      capacity: resource.capacity || 1
                    });
                    setDeleteConfirmStep(0);
                    setResourceDialogOpen(true);
                  }}
                >
                  <Edit className="h-4 w-4 text-gray-500 hover:text-blue-600" />
                </button>
              )}
              {['admin', 'manager'].includes(userRole) && (
                <button
                  className="p-1 rounded hover:bg-gray-100"
                  title="Defekt melden / sperren"
                  data-testid={`resource-block-btn-${resource.id}`}
                  onClick={() => openBlockDialog(resource)}
                >
                  <Wrench className="h-4 w-4 text-gray-500 hover:text-amber-600" />
                </button>
              )}
              {activeBlocks[resource.id] && (
                <span
                  className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-red-100 text-red-700 text-[10px] font-semibold border border-red-200"
                  title={activeBlocks[resource.id].reason || 'Gesperrt'}
                  data-testid={`resource-blocked-badge-${resource.id}`}
                >
                  <AlertTriangle className="h-3 w-3" />
                  Defekt
                </span>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Block C: Ressourcen-Auslastung (Tag/Woche) */}
      {showUtilization && (
        <Card data-testid="utilization-panel">
          <CardHeader className="pb-3">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
              <div className="flex items-center gap-2">
                <CardTitle className="text-base">Ressourcen-Auslastung</CardTitle>
                <div className="flex rounded-md border overflow-hidden">
                  <button
                    className={`px-2.5 py-1 text-xs ${utilView === 'day' ? 'bg-gray-900 text-white' : 'bg-white text-gray-600'}`}
                    onClick={() => setUtilView('day')} data-testid="util-view-day">Tag</button>
                  <button
                    className={`px-2.5 py-1 text-xs ${utilView === 'week' ? 'bg-gray-900 text-white' : 'bg-white text-gray-600'}`}
                    onClick={() => setUtilView('week')} data-testid="util-view-week">Woche</button>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="icon" className="h-8 w-8"
                  onClick={() => setUtilDate((d) => addDays(new Date(d), utilView === 'week' ? -7 : -1).toISOString().slice(0, 10))}
                  data-testid="util-prev">
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Input type="date" value={utilDate}
                  onChange={(e) => setUtilDate(e.target.value)}
                  className="w-auto h-8 text-sm" data-testid="util-date-input" />
                <Button variant="outline" size="icon" className="h-8 w-8"
                  onClick={() => setUtilDate((d) => addDays(new Date(d), utilView === 'week' ? 7 : 1).toISOString().slice(0, 10))}
                  data-testid="util-next">
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <div className="flex items-center gap-3 mt-2 text-xs text-gray-500 flex-wrap">
              <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-blue-500" /> Termin</span>
              <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-emerald-500" /> Reservation</span>
              <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-red-500" /> Gesperrt / Defekt</span>
              {utilView === 'day' && <span>Fenster 06–23 Uhr</span>}
            </div>
          </CardHeader>
          <CardContent className="px-3 sm:px-6 pb-4">
            {utilLoading ? (
              <p className="text-sm text-gray-500 py-6 text-center">Lade Auslastung…</p>
            ) : utilView === 'day' ? (
              utilization.length === 0 ? (
                <p className="text-sm text-gray-500 py-6 text-center">Keine Ressourcen gefunden.</p>
              ) : (
                <div className="space-y-2">
                  {/* Stundenachse */}
                  <div className="flex items-center gap-2">
                    <div className="w-32 shrink-0" />
                    <div className="relative flex-1 h-4">
                      {[6, 9, 12, 15, 18, 21].map((h) => (
                        <span key={h} className="absolute text-[10px] text-gray-400 -translate-x-1/2"
                          style={{ left: `${((h - 6) / 17) * 100}%` }}>{h}:00</span>
                      ))}
                    </div>
                    <div className="w-9 shrink-0" />
                  </div>
                  {utilization.map((r) => {
                    const START = 6, SPAN = 17;
                    const segStyle = (s, e) => {
                      let sh = swissHour(s);
                      let eh = swissHour(e);
                      if (eh <= sh) eh = START + SPAN;
                      const left = Math.max(0, Math.min(100, ((sh - START) / SPAN) * 100));
                      const right = Math.max(0, Math.min(100, ((eh - START) / SPAN) * 100));
                      return { left: `${left}%`, width: `${Math.max(1.5, right - left)}%` };
                    };
                    const blocked = (r.blocks || []).length > 0;
                    return (
                      <div key={r.id} className="flex items-center gap-2" data-testid={`util-row-${r.id}`}>
                        <div className="w-32 shrink-0 flex items-center gap-1.5 text-xs truncate">
                          <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: r.color || '#3B82F6' }} />
                          <span className="truncate font-medium">{r.name}</span>
                          {r.capacity > 1 && <span className="text-gray-400">×{r.capacity}</span>}
                        </div>
                        <div className="relative flex-1 h-8 bg-gray-50 rounded border border-gray-100 overflow-hidden">
                          {(r.busy || []).map((seg, i) => (
                            <div key={`b${i}`}
                              className={`absolute top-1 bottom-1 rounded-sm ${seg.kind === 'reservation' ? 'bg-emerald-500' : 'bg-blue-500'}`}
                              style={segStyle(seg.start, seg.end)}
                              title={`${seg.label}`} />
                          ))}
                          {(r.blocks || []).map((seg, i) => (
                            <div key={`k${i}`}
                              className="absolute top-0 bottom-0 bg-red-500/70 border border-red-600"
                              style={{ ...segStyle(seg.start, seg.end), backgroundImage: 'repeating-linear-gradient(45deg,transparent,transparent 4px,rgba(255,255,255,.35) 4px,rgba(255,255,255,.35) 8px)' }}
                              title={`Gesperrt: ${seg.label}`} />
                          ))}
                          {!blocked && (r.busy || []).length === 0 && (
                            <span className="absolute inset-0 flex items-center justify-center text-[10px] text-gray-400">frei</span>
                          )}
                        </div>
                        <button
                          className={`w-9 h-8 shrink-0 flex items-center justify-center rounded border text-xs ${blocked ? 'border-red-300 text-red-600 bg-red-50' : 'border-gray-200 text-gray-400 hover:text-amber-600 hover:border-amber-300'}`}
                          title="Defekt melden / sperren"
                          onClick={() => openBlockDialog({ id: r.id, name: r.name })}
                          data-testid={`util-defect-${r.id}`}>
                          <Wrench className="h-4 w-4" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )
            ) : (
              // Wochenansicht
              (() => {
                const roster = (weekUtil.find((d) => (d.resources || []).length) || {}).resources || [];
                if (roster.length === 0) return <p className="text-sm text-gray-500 py-6 text-center">Keine Ressourcen gefunden.</p>;
                const dayNames = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];
                return (
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs border-collapse min-w-[560px]">
                      <thead>
                        <tr>
                          <th className="text-left font-medium text-gray-500 w-32 pb-2">Ressource</th>
                          {weekUtil.map((d, i) => (
                            <th key={d.date} className="font-medium text-gray-500 pb-2 px-1">
                              <div>{dayNames[i]}</div>
                              <div className="text-gray-400">{new Date(d.date).getDate()}.{new Date(d.date).getMonth() + 1}.</div>
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {roster.map((r) => (
                          <tr key={r.id} data-testid={`util-week-row-${r.id}`}>
                            <td className="py-1 pr-2">
                              <div className="flex items-center gap-1.5 truncate">
                                <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: r.color || '#3B82F6' }} />
                                <span className="truncate font-medium">{r.name}</span>
                              </div>
                            </td>
                            {weekUtil.map((d) => {
                              const cell = (d.resources || []).find((x) => x.id === r.id) || {};
                              const busyCount = (cell.busy || []).length;
                              const blocked = (cell.blocks || []).length > 0;
                              const cls = blocked
                                ? 'bg-red-100 text-red-700 border-red-200'
                                : busyCount === 0
                                  ? 'bg-gray-50 text-gray-300 border-gray-100'
                                  : busyCount <= 2 ? 'bg-emerald-100 text-emerald-700 border-emerald-200'
                                    : busyCount <= 4 ? 'bg-emerald-200 text-emerald-800 border-emerald-300'
                                      : 'bg-emerald-400 text-white border-emerald-500';
                              return (
                                <td key={d.date} className="p-0.5">
                                  <button
                                    onClick={() => { setUtilDate(d.date); setUtilView('day'); }}
                                    title={blocked ? 'Gesperrt / Defekt' : `${busyCount} Belegung(en)`}
                                    className={`w-full h-9 rounded border flex items-center justify-center font-medium ${cls}`}>
                                    {blocked ? <Wrench className="h-3.5 w-3.5" /> : busyCount === 0 ? '–' : busyCount}
                                  </button>
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <p className="text-[11px] text-gray-400 mt-2">Zahl = Anzahl Belegungen pro Tag · Klick öffnet den Tag · rot = gesperrt/defekt</p>
                  </div>
                );
              })()
            )}
          </CardContent>
        </Card>
      )}

      {/* Calendar */}
      {selectedResource ? (
        <Card>
          {activeBlocks[selectedResource.id] && (
            <div className="flex items-center gap-2 px-4 py-2 bg-red-50 border-b border-red-200 text-red-700 text-sm"
              data-testid="selected-resource-block-banner">
              <AlertTriangle className="h-4 w-4 shrink-0" />
              <span>
                <strong>Gesperrt / Defekt:</strong> {activeBlocks[selectedResource.id].reason || 'Diese Ressource ist aktuell gesperrt.'}
                {!activeBlocks[selectedResource.id].end && ' (bis auf Weiteres)'}
              </span>
            </div>
          )}
          <CardHeader className="pb-2 px-3 sm:px-6">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
              {/* Navigation controls */}
              <div className="flex items-center gap-2 sm:gap-4 w-full sm:w-auto">
                <Button variant="outline" size="icon" onClick={goToPreviousWeek} className="h-8 w-8 sm:h-10 sm:w-10">
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button variant="outline" onClick={goToToday} size="sm" className="text-xs sm:text-sm">Heute</Button>
                <Button variant="outline" size="icon" onClick={goToNextWeek} className="h-8 w-8 sm:h-10 sm:w-10">
                  <ChevronRight className="h-4 w-4" />
                </Button>
                <span className="font-medium text-xs sm:text-base whitespace-nowrap">
                  {formatDate(weekDates[0])} - {formatDate(weekDates[6], true)}
                </span>
              </div>
              {/* Add booking button */}
              <Button 
                className="w-full sm:w-auto text-sm"
                onClick={() => {
                  resetBookingForm();
                  setBookingForm(prev => ({
                    ...prev,
                    date: new Date().toISOString().split('T')[0]
                  }));
                  setEditingBooking(null);
                  setBookingDialogOpen(true);
                }}>
                <Plus className="h-4 w-4 mr-2" />
                Buchung hinzufügen
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-0 overflow-x-auto">
            {/* Calendar Grid - scrollable on mobile */}
            <div className="min-w-[700px] lg:min-w-[800px]">
              {/* Header row with days */}
              <div className="grid grid-cols-8 border-b">
                <div className="p-2 text-center text-xs font-medium text-gray-500 border-r bg-gray-50">
                  Zeit
                </div>
                {weekDates.map((date, idx) => (
                  <div 
                    key={idx} 
                    className={`p-2 text-center border-r ${isToday(date) ? 'bg-blue-50' : 'bg-gray-50'}`}
                  >
                    <div className="text-xs font-medium text-gray-500">{DAYS[idx]}</div>
                    <div className={`text-sm font-bold ${isToday(date) ? 'text-blue-600' : ''}`}>
                      {formatDate(date)}
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Time slots */}
              {HOURS.map(hour => (
                <div key={hour} className="grid grid-cols-8 border-b" style={{ minHeight: '50px' }}>
                  <div className="p-1 text-xs text-gray-500 border-r bg-gray-50 flex items-center justify-center">
                    {hour.toString().padStart(2, '0')}:00
                  </div>
                  {weekDates.map((date, dayIdx) => {
                    const cellBookings = getBookingsForCell(date, hour);
                    
                    return (
                      <div 
                        key={dayIdx} 
                        className={`border-r relative cursor-pointer hover:bg-gray-50 ${isToday(date) ? 'bg-blue-50/30' : ''}`}
                        onClick={() => cellBookings.length === 0 && handleCellClick(date, hour)}
                      >
                        {cellBookings.map((booking, bIdx) => {
                          const start = new Date(booking.start_time);
                          const end = new Date(booking.end_time);
                          const startHour = start.getHours() + start.getMinutes() / 60;
                          const endHour = end.getHours() + end.getMinutes() / 60;
                          
                          // Only render if this is the starting hour
                          if (Math.floor(startHour) !== hour) return null;
                          
                          const duration = endHour - startHour;
                          const topOffset = (startHour - hour) * 100;
                          const height = duration * 50;
                          
                          return (
                            <div
                              key={bIdx}
                              className="absolute left-0 right-0 mx-1 rounded px-1 py-0.5 text-xs text-white overflow-hidden cursor-pointer group"
                              style={{
                                backgroundColor: booking.color || selectedResource.color || '#3B82F6',
                                top: `${topOffset}%`,
                                height: `${height}px`,
                                zIndex: 10
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEditBooking(booking);
                              }}
                            >
                              <div className="font-medium truncate flex items-center gap-1">
                                {booking.is_recurring && <Repeat className="h-3 w-3 flex-shrink-0" />}
                                {booking.is_private && <Lock className="h-3 w-3 flex-shrink-0" />}
                                {booking.is_private ? 'Reserviert' : booking.title}
                              </div>
                              <div className="text-[10px] opacity-80 truncate">
                                {booking.is_private ? '' : booking.booked_by_name}
                              </div>
                              <div className="text-[10px] opacity-80">
                                {start.toTimeString().slice(0, 5)} - {end.toTimeString().slice(0, 5)}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-8 text-center text-gray-500">
            {resources.length === 0 ? (
              <>
                <Calendar className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p>Noch keine Ressourcen vorhanden.</p>
                {['admin', 'trainer'].includes(userRole) && (
                  <Button className="mt-4" onClick={() => setResourceDialogOpen(true)}>
                    <Plus className="h-4 w-4 mr-2" />
                    Erste Ressource erstellen
                  </Button>
                )}
              </>
            ) : (
              <p>Wählen Sie eine Ressource aus</p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Resource Dialog */}
      <Dialog open={resourceDialogOpen} onOpenChange={(open) => {
        setResourceDialogOpen(open);
        if (!open) setDeleteConfirmStep(0);
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingResource ? 'Ressource bearbeiten' : 'Neue Ressource'}</DialogTitle>
            <DialogDescription>
              {editingResource ? 'Bearbeiten Sie die Details der Ressource' : 'Fügen Sie eine neue Ressource hinzu'}
            </DialogDescription>
          </DialogHeader>
          
          {/* Delete confirmation steps */}
          {deleteConfirmStep > 0 && editingResource && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
              <div className="flex items-center gap-2 text-red-700 font-medium mb-2">
                <Trash2 className="h-5 w-5" />
                <span>Ressource löschen - Schritt {deleteConfirmStep} von 3</span>
              </div>
              <p className="text-sm text-red-600 mb-3">
                {deleteConfirmStep === 1 && 'Sind Sie sicher, dass Sie diese Ressource löschen möchten?'}
                {deleteConfirmStep === 2 && 'ACHTUNG: Alle zugehörigen Buchungen werden ebenfalls gelöscht!'}
                {deleteConfirmStep === 3 && `Letzte Warnung: "${editingResource.name}" wird unwiderruflich gelöscht!`}
              </p>
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setDeleteConfirmStep(0)}
                >
                  Abbrechen
                </Button>
                <Button 
                  variant="destructive" 
                  size="sm"
                  onClick={() => {
                    if (deleteConfirmStep < 3) {
                      setDeleteConfirmStep(deleteConfirmStep + 1);
                    } else {
                      handleDeleteResource(editingResource.id);
                      setResourceDialogOpen(false);
                      setDeleteConfirmStep(0);
                    }
                  }}
                >
                  {deleteConfirmStep < 3 ? `Bestätigen (${3 - deleteConfirmStep} verbleibend)` : 'Endgültig löschen'}
                </Button>
              </div>
            </div>
          )}
          
          <div className="space-y-4">
            <div>
              <Label>Name *</Label>
              <Input
                value={resourceForm.name}
                onChange={(e) => setResourceForm({ ...resourceForm, name: e.target.value })}
                placeholder="z.B. Studio, Trainingsraum 1"
              />
            </div>
            <div>
              <Label>Standort / Studio</Label>
              <Select
                value={resourceForm.studio_id || 'none'}
                onValueChange={(v) => setResourceForm({ ...resourceForm, studio_id: v === 'none' ? '' : v })}
              >
                <SelectTrigger data-testid="resource-studio-select">
                  <SelectValue placeholder="Studio wählen..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Kein Studio (überall sichtbar)</SelectItem>
                  {studios.map(s => (
                    <SelectItem key={s.id} value={s.id}>{s.short_name || s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 mt-1">Bestimmt, in welchem Standort-Kursplan (iFrame) die Kurse dieses Raums erscheinen.</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Typ</Label>
                <Select
                  value={resourceForm.type || 'room'}
                  onValueChange={(v) => setResourceForm({ ...resourceForm, type: v })}
                >
                  <SelectTrigger data-testid="resource-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="room">Raum</SelectItem>
                    <SelectItem value="device">Gerät</SelectItem>
                    <SelectItem value="seat">Platz</SelectItem>
                    <SelectItem value="station">Station</SelectItem>
                    <SelectItem value="lift">Hebebühne</SelectItem>
                    <SelectItem value="chair">Behandlungsstuhl</SelectItem>
                    <SelectItem value="hall">Saal</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500 mt-1">Für flexible Zuweisung nach Typ in Leistungen.</p>
              </div>
              <div>
                <Label>Kapazität</Label>
                <Input
                  type="number"
                  min={1}
                  data-testid="resource-capacity-input"
                  value={resourceForm.capacity || 1}
                  onChange={(e) => setResourceForm({ ...resourceForm, capacity: Math.max(1, parseInt(e.target.value) || 1) })}
                />
                <p className="text-xs text-gray-500 mt-1">Wie viele Termine gleichzeitig (z. B. Gerät mehrfach nutzbar).</p>
              </div>
            </div>
            <div>
              <Label>Beschreibung</Label>
              <Textarea
                value={resourceForm.description}
                onChange={(e) => setResourceForm({ ...resourceForm, description: e.target.value })}
                placeholder="Optionale Beschreibung..."
              />
            </div>
            <div>
              <Label>Farbe</Label>
              <div className="flex gap-2 mt-1">
                {['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'].map(color => (
                  <button
                    key={color}
                    className={`w-8 h-8 rounded-full border-2 ${resourceForm.color === color ? 'border-gray-900' : 'border-transparent'}`}
                    style={{ backgroundColor: color }}
                    onClick={() => setResourceForm({ ...resourceForm, color })}
                  />
                ))}
              </div>
            </div>
          </div>
          
          <DialogFooter className="flex justify-between sm:justify-between">
            {/* Delete button - only for existing resources and admins */}
            {editingResource && userRole === 'admin' && deleteConfirmStep === 0 ? (
              <Button 
                variant="outline" 
                className="text-red-600 border-red-300 hover:bg-red-50 hover:text-red-700"
                onClick={() => setDeleteConfirmStep(1)}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Löschen
              </Button>
            ) : (
              <div></div>
            )}
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => {
                setResourceDialogOpen(false);
                setDeleteConfirmStep(0);
              }}>Abbrechen</Button>
              <Button onClick={handleSaveResource} disabled={!resourceForm.name || deleteConfirmStep > 0}>Speichern</Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Block C: Defekt-Meldung / Ressource sperren */}
      <Dialog open={blockDialogOpen} onOpenChange={setBlockDialogOpen}>
        <DialogContent data-testid="resource-block-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wrench className="h-5 w-5 text-amber-600" />
              Defekt melden / sperren{blockResource ? `: ${blockResource.name}` : ''}
            </DialogTitle>
            <DialogDescription>
              Eine Sperre blockt automatisch alle Buchungen dieser Ressource im Zeitraum. Ohne Enddatum gilt sie bis auf Weiteres.
            </DialogDescription>
          </DialogHeader>

          {resourceBlocks.length > 0 && (
            <div className="space-y-2 mb-3" data-testid="resource-block-list">
              <p className="text-sm font-medium text-gray-700">Aktive Sperren</p>
              {resourceBlocks.map((b) => (
                <div key={b.id} className="flex items-center justify-between bg-amber-50 border border-amber-200 rounded-lg p-2 text-sm" data-testid={`resource-block-item-${b.id}`}>
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0" />
                    <div>
                      <div className="font-medium text-amber-800">{b.reason || 'Gesperrt'}</div>
                      <div className="text-xs text-amber-700">
                        {b.start ? new Date(b.start).toLocaleString('de-CH') : '—'}
                        {' – '}
                        {b.end ? new Date(b.end).toLocaleString('de-CH') : 'bis auf Weiteres'}
                      </div>
                    </div>
                  </div>
                  <button
                    className="text-amber-600 hover:text-red-600 p-1"
                    title="Sperre aufheben"
                    data-testid={`resource-block-remove-${b.id}`}
                    onClick={() => handleDeleteBlock(b.id)}
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Von (optional)</Label>
                <Input
                  type="datetime-local"
                  data-testid="resource-block-start"
                  value={blockForm.start}
                  onChange={(e) => setBlockForm({ ...blockForm, start: e.target.value })}
                />
                <p className="text-xs text-gray-500 mt-1">Leer = ab sofort</p>
              </div>
              <div>
                <Label>Bis (optional)</Label>
                <Input
                  type="datetime-local"
                  data-testid="resource-block-end"
                  value={blockForm.end}
                  onChange={(e) => setBlockForm({ ...blockForm, end: e.target.value })}
                />
                <p className="text-xs text-gray-500 mt-1">Leer = bis auf Weiteres</p>
              </div>
            </div>
            <div>
              <Label>Grund</Label>
              <Input
                data-testid="resource-block-reason"
                value={blockForm.reason}
                onChange={(e) => setBlockForm({ ...blockForm, reason: e.target.value })}
                placeholder="z. B. Gerät defekt, Wartung"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setBlockDialogOpen(false)} data-testid="resource-block-close">Schließen</Button>
            <Button onClick={handleSaveBlock} className="bg-amber-600 hover:bg-amber-700" data-testid="resource-block-save">
              <Wrench className="h-4 w-4 mr-2" /> Sperren
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Booking Dialog */}
      <Dialog open={bookingDialogOpen} onOpenChange={setBookingDialogOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>{editingBooking ? 'Buchung bearbeiten' : 'Neue Buchung'}</span>
              <div className="flex items-center gap-1">
                {editingBooking && editingBooking.is_recurring && (
                  <Button
                    variant="ghost"
                    size="icon"
                    data-testid="booking-cancel-occurrence-btn"
                    className="text-amber-600 hover:text-amber-700"
                    title="Nur diesen Termin absagen"
                    onClick={() => handleCancelOccurrence(editingBooking)}
                  >
                    <CalendarX2 className="h-4 w-4" />
                  </Button>
                )}
                {editingBooking && (
                  <Button 
                    variant="ghost" 
                    size="icon"
                    data-testid="booking-delete-btn"
                    className="text-red-500 hover:text-red-700"
                    title={editingBooking.is_recurring ? 'Ganze Serie löschen' : 'Buchung löschen'}
                    onClick={() => {
                      handleDeleteBooking(editingBooking);
                      setBookingDialogOpen(false);
                    }}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </DialogTitle>
            <DialogDescription>
              {editingBooking ? 'Bearbeiten Sie die Buchungsdetails' : 'Erstellen Sie eine neue Buchung'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Titel *</Label>
              <Input
                value={bookingForm.title}
                onChange={(e) => setBookingForm({ ...bookingForm, title: e.target.value })}
                placeholder="z.B. Yoga Kurs, Personal Training"
              />
            </div>
            <div>
              <Label>Bemerkung</Label>
              <Textarea
                value={bookingForm.description}
                onChange={(e) => setBookingForm({ ...bookingForm, description: e.target.value })}
                placeholder="Optionale Bemerkung..."
              />
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label>Datum *</Label>
                <Input
                  type="date"
                  value={bookingForm.date}
                  onChange={(e) => setBookingForm({ ...bookingForm, date: e.target.value })}
                />
              </div>
              <div>
                <Label>Von *</Label>
                <Input
                  type="time"
                  value={bookingForm.startTime}
                  onChange={(e) => setBookingForm({ ...bookingForm, startTime: e.target.value })}
                />
              </div>
              <div>
                <Label>Bis *</Label>
                <Input
                  type="time"
                  value={bookingForm.endTime}
                  onChange={(e) => setBookingForm({ ...bookingForm, endTime: e.target.value })}
                />
              </div>
            </div>
            
            {/* Contact & Registration Info Section */}
            <div className="border-t pt-4 mt-4">
              <h4 className="font-medium text-sm mb-3 text-gray-700">📢 Kontakt & Anmeldung (öffentlich sichtbar)</h4>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Trainer/Kontakt Name</Label>
                  <Input
                    value={bookingForm.contactName}
                    onChange={(e) => setBookingForm({ ...bookingForm, contactName: e.target.value })}
                    placeholder="z.B. Maria Müller"
                  />
                </div>
                <div>
                  <Label className="text-xs">Telefon</Label>
                  <Input
                    value={bookingForm.contactPhone}
                    onChange={(e) => setBookingForm({ ...bookingForm, contactPhone: e.target.value })}
                    placeholder="z.B. +41 79 123 45 67"
                  />
                </div>
                <div>
                  <Label className="text-xs">E-Mail</Label>
                  <Input
                    type="email"
                    value={bookingForm.contactEmail}
                    onChange={(e) => setBookingForm({ ...bookingForm, contactEmail: e.target.value })}
                    placeholder="trainer@beispiel.ch"
                  />
                </div>
                <div>
                  <Label className="text-xs">Website</Label>
                  <Input
                    value={bookingForm.contactWebsite}
                    onChange={(e) => setBookingForm({ ...bookingForm, contactWebsite: e.target.value })}
                    placeholder="https://..."
                  />
                </div>
                <div>
                  <Label className="text-xs">Max. Teilnehmer</Label>
                  <Input
                    type="number"
                    value={bookingForm.maxParticipants}
                    onChange={(e) => setBookingForm({ ...bookingForm, maxParticipants: e.target.value })}
                    placeholder="z.B. 15"
                  />
                </div>
                <div>
                  <Label className="text-xs">Aktuelle Anmeldungen</Label>
                  <Input
                    type="number"
                    value={bookingForm.currentParticipants}
                    onChange={(e) => setBookingForm({ ...bookingForm, currentParticipants: e.target.value })}
                    placeholder="z.B. 15 = ausgebucht"
                    data-testid="booking-current-participants"
                  />
                  <p className="text-[10px] text-gray-500 mt-0.5">Bei voller Auslastung zeigt der Kursplan „Ausgebucht"</p>
                </div>
                <div>
                  <Label className="text-xs">Anmelde-Link</Label>
                  <Input
                    value={bookingForm.registrationLink}
                    onChange={(e) => setBookingForm({ ...bookingForm, registrationLink: e.target.value })}
                    placeholder="https://anmeldung..."
                  />
                </div>
              </div>
              
              <div className="mt-3">
                <Label className="text-xs">Anmelde-Info / Call-to-Action</Label>
                <Input
                  value={bookingForm.registrationInfo}
                  onChange={(e) => setBookingForm({ ...bookingForm, registrationInfo: e.target.value })}
                  placeholder="z.B. Ruf uns an! oder Jetzt anmelden!"
                />
              </div>

              <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg mt-3">
                <div>
                  <span className="text-sm font-medium">Im öffentlichen Kursplan anzeigen</span>
                  <p className="text-xs text-gray-500">Zeigt diesen Kurs auf der Website</p>
                </div>
                <Switch
                  checked={bookingForm.isPublic}
                  onCheckedChange={(checked) => setBookingForm({ ...bookingForm, isPublic: checked })}
                />
              </div>
            </div>

            {/* Recurring options */}
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-2">
                <Repeat className="h-4 w-4 text-gray-500" />
                <span className="text-sm">Wiederkehrend</span>
              </div>
              <Switch
                checked={bookingForm.isRecurring}
                onCheckedChange={(checked) => setBookingForm({ ...bookingForm, isRecurring: checked })}
              />
            </div>
            
            {bookingForm.isRecurring && (
              <div>
                <Label>Wiederholung</Label>
                <Select
                  value={bookingForm.recurringType}
                  onValueChange={(value) => setBookingForm({ ...bookingForm, recurringType: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="daily">Täglich</SelectItem>
                    <SelectItem value="weekly">Wöchentlich</SelectItem>
                    <SelectItem value="biweekly">Alle 2 Wochen</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500 mt-1">
                  {bookingForm.recurringUntil ? 'Wiederholt sich bis zum gewählten Datum' : 'Wiederholt sich bis auf Widerruf'}
                </p>
                <div className="mt-3">
                  <Label>Wiederholen bis (optional)</Label>
                  <Input
                    type="date"
                    data-testid="booking-recurring-until"
                    value={bookingForm.recurringUntil}
                    min={bookingForm.date || undefined}
                    onChange={(e) => setBookingForm({ ...bookingForm, recurringUntil: e.target.value })}
                  />
                  <p className="text-xs text-gray-500 mt-1">Leer lassen für unbegrenzte Wiederholung.</p>
                </div>
              </div>
            )}

            {/* Privatnutzung (Physio / Blockierung) */}
            <div className="flex items-center justify-between p-3 bg-amber-50 rounded-lg">
              <div className="flex items-center gap-2">
                <Lock className="h-4 w-4 text-amber-600" />
                <div>
                  <span className="text-sm font-medium">Privatnutzung / Raum blockieren</span>
                  <p className="text-xs text-gray-500">Erscheint nur als „Reserviert", nicht öffentlich</p>
                </div>
              </div>
              <Switch
                data-testid="booking-private-switch"
                checked={bookingForm.isPrivate}
                onCheckedChange={(checked) => setBookingForm({ ...bookingForm, isPrivate: checked, isPublic: checked ? false : bookingForm.isPublic })}
              />
            </div>

            {/* E-Mail-Bestätigung an reservierende Person */}
            <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg" data-testid="booking-notify-email-row">
              <div>
                <span className="text-sm font-medium">E-Mail senden</span>
                <p className="text-xs text-gray-500">Bestätigung / Storno / Verschiebung per E-Mail (nur mit E-Mail-Adresse)</p>
              </div>
              <Switch
                data-testid="booking-notify-email"
                checked={bookingForm.notifyEmail}
                onCheckedChange={(checked) => setBookingForm({ ...bookingForm, notifyEmail: checked })}
              />
            </div>

            {/* WhatsApp-Bestätigung an reservierende Person */}
            <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
              <div>
                <span className="text-sm font-medium">WhatsApp senden</span>
                <p className="text-xs text-gray-500">Einwilligung der Person; nur mit Mobilnummer (sonst nur E-Mail/.ics)</p>
              </div>
              <Switch
                data-testid="booking-whatsapp-optin"
                checked={bookingForm.whatsappOptin}
                onCheckedChange={(checked) => setBookingForm({ ...bookingForm, whatsappOptin: checked })}
              />
            </div>

            {editingBooking?.is_recurring && (
              <div className="p-3 bg-amber-50 rounded-lg" data-testid="booking-edit-scope">
                <span className="text-sm font-medium">Änderung anwenden auf</span>
                <div className="flex gap-2 mt-2">
                  <button
                    type="button"
                    data-testid="edit-scope-all"
                    onClick={() => setEditScope('all')}
                    className={`flex-1 text-xs px-3 py-2 rounded-md border transition-colors ${editScope === 'all' ? 'bg-amber-600 text-white border-amber-600' : 'bg-white text-gray-700 border-gray-300'}`}
                  >
                    Alle Termine
                  </button>
                  <button
                    type="button"
                    data-testid="edit-scope-following"
                    onClick={() => setEditScope('this_and_following')}
                    className={`flex-1 text-xs px-3 py-2 rounded-md border transition-colors ${editScope === 'this_and_following' ? 'bg-amber-600 text-white border-amber-600' : 'bg-white text-gray-700 border-gray-300'}`}
                  >
                    Diesen und alle folgenden
                  </button>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" data-testid="booking-cancel-btn" onClick={() => setBookingDialogOpen(false)}>Abbrechen</Button>
            <Button 
              data-testid="booking-save-btn"
              onClick={handleSaveBooking} 
              disabled={!bookingForm.title || !bookingForm.date || !bookingForm.startTime || !bookingForm.endTime}
            >
              Speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Embed Dialog */}
      <Dialog open={embedDialogOpen} onOpenChange={setEmbedDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Code className="h-5 w-5" />
              Kursplan einbetten
            </DialogTitle>
            <DialogDescription>
              Betten Sie den öffentlichen Kursplan auf Ihrer Website ein
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Kopieren Sie den folgenden Code, um den öffentlichen Kursplan auf Ihrer Website einzubetten.
            </p>

            {/* Studio-Auswahl: verhindert Vermischung mehrerer Standorte */}
            <div>
              <Label className="mb-2 block">Standort im Plan</Label>
              <Select value={embedStudioId || 'all'} onValueChange={(v) => setEmbedStudioId(v === 'all' ? '' : v)}>
                <SelectTrigger data-testid="embed-studio-select">
                  <SelectValue placeholder="Alle Standorte" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle Standorte</SelectItem>
                  {studios.map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.short_name || s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 mt-1">
                Der Link enthält automatisch Ihre Kennung
                {embedTenantSlug ? ` „${embedTenantSlug}“` : ''}, damit keine Daten anderer Kunden erscheinen.
              </p>
            </div>

            {/* Preview Link */}
            <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-blue-900">Vorschau öffnen</p>
                  <p className="text-xs text-blue-700">Sehen Sie, wie der Kursplan aussieht</p>
                </div>
                <a
                  href={buildKursplanUrl(false)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
                  data-testid="embed-preview-link"
                >
                  <ExternalLink className="h-4 w-4" />
                  Öffnen
                </a>
              </div>
            </div>

            {/* iFrame Code */}
            <div>
              <Label className="mb-2 block">iFrame Code</Label>
              <div className="relative">
                <Textarea
                  readOnly
                  data-testid="embed-iframe-code"
                  value={`<iframe 
  src="${buildKursplanUrl(true)}" 
  width="100%" 
  height="700" 
  style="border: none; border-radius: 8px;"
  title="Kursplan"
></iframe>`}
                  className="font-mono text-sm bg-gray-900 text-green-400 h-32"
                />
                <Button
                  size="sm"
                  variant="secondary"
                  className="absolute top-2 right-2"
                  onClick={() => {
                    navigator.clipboard.writeText(`<iframe src="${buildKursplanUrl(true)}" width="100%" height="700" style="border: none; border-radius: 8px;" title="Kursplan"></iframe>`);
                    setCopiedEmbed(true);
                    toast.success('Code kopiert!');
                    setTimeout(() => setCopiedEmbed(false), 2000);
                  }}
                >
                  {copiedEmbed ? (
                    <><Check className="h-4 w-4 mr-1" /> Kopiert</>
                  ) : (
                    <><Copy className="h-4 w-4 mr-1" /> Kopieren</>
                  )}
                </Button>
              </div>
            </div>

            {/* Direct Link */}
            <div>
              <Label className="mb-2 block">Direkter Link</Label>
              <div className="flex gap-2">
                <Input
                  readOnly
                  data-testid="embed-direct-link"
                  value={buildKursplanUrl(false)}
                  className="font-mono text-sm"
                />
                <Button
                  variant="outline"
                  onClick={() => {
                    navigator.clipboard.writeText(buildKursplanUrl(false));
                    toast.success('Link kopiert!');
                  }}
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* Instructions */}
            <div className="p-3 bg-gray-50 rounded-lg text-sm">
              <p className="font-medium mb-2">Anleitung:</p>
              <ol className="list-decimal list-inside space-y-1 text-gray-600">
                <li>Kopieren Sie den iFrame-Code oben</li>
                <li>Fügen Sie den Code in Ihre Website ein (z.B. in WordPress, Squarespace, etc.)</li>
                <li>Der Kursplan aktualisiert sich automatisch mit Ihren Buchungen</li>
              </ol>
            </div>

            <div className="p-3 bg-yellow-50 rounded-lg border border-yellow-200">
              <p className="text-sm text-yellow-800">
                <strong>Tipp:</strong> Nur Buchungen mit "Im öffentlichen Kursplan anzeigen" aktiviert werden angezeigt.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button onClick={() => setEmbedDialogOpen(false)}>Schliessen</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ResourcesPage;
